/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Campaign, Lookup } from '../../api/types/types';
import { CampaignFormData } from './types';
import { buildValidationSchema } from './yupValidationSchema';
import {
  getFilteredLookupForResponseCode,
  getValuesForResponseCode,
  isValueValidForResponseCode,
} from './campaign-response-lookup-utils';

export interface UseCampaignFormOptions {
  campaigns?: Campaign[];
  reasonLookup?: Lookup;
  typeLookup?: Lookup;
  competitorLookup?: Lookup;
  resetOnOpen?: boolean;
  open?: boolean;
}

export interface UseCampaignFormReturn {
  methods: ReturnType<typeof useForm<Record<string, CampaignFormData>>>;
  validateForm: () => Promise<boolean>;
  sortedCompetitorOptions: Array<{ label?: string; id?: string; value?: number }>;
  getFilteredReasonLookup: (
    responseCodeValue: string | null | undefined,
  ) => { lookup: Lookup | undefined; hasReasons: boolean };
  getFilteredTypeLookup: (
    responseCodeValue: string | null | undefined,
  ) => { lookup: Lookup | undefined; hasTypes: boolean };
  handleResponseCodeChange: (campaignKey: string, value: string) => void;
}

// Stable empty arrays to prevent infinite loops
const EMPTY_CAMPAIGNS: Campaign[] = [];

const getCampaignsKey = (campaigns: Campaign[]): string => {
  if (campaigns.length === 0) {
    return 'empty';
  }
  return campaigns.map((c, i) => c.campaignId || i.toString()).join(',');
};

export const useCampaignForm = ({
  campaigns,
  reasonLookup,
  typeLookup,
  competitorLookup,
  resetOnOpen = false,
  open,
}: UseCampaignFormOptions): UseCampaignFormReturn => {
  const prevCampaignsRef = useRef<Campaign[]>(EMPTY_CAMPAIGNS);
  const prevCampaignsKeyRef = useRef<string>('empty');

  const memoizedCampaigns = useMemo(() => {
    const stableCampaigns = campaigns ?? EMPTY_CAMPAIGNS;
    const currentKey = getCampaignsKey(stableCampaigns);
    
    if (currentKey === prevCampaignsKeyRef.current && stableCampaigns.length === prevCampaignsRef.current.length) {
      return prevCampaignsRef.current;
    }
    
    prevCampaignsRef.current = stableCampaigns;
    prevCampaignsKeyRef.current = currentKey;
    return stableCampaigns;
  }, [campaigns]);

  const defaultValues = useMemo(() => {
    return memoizedCampaigns.reduce((acc, campaign, index) => {
      const campaignKey = campaign.campaignId || index.toString();
      acc[campaignKey] = { responseCode: '', reason: '', type: '', competitor: '', notes: '' };
      return acc;
    }, {} as Record<string, CampaignFormData>);
  }, [memoizedCampaigns]);

  const validationSchema = useMemo(
    () => buildValidationSchema(memoizedCampaigns, reasonLookup, typeLookup),
    [memoizedCampaigns, reasonLookup, typeLookup],
  );

  const methods = useForm<Record<string, CampaignFormData>>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const {
    reset,
    setValue,
    getValues,
    trigger,
    clearErrors,
  } = methods;

  const validateForm = useCallback(async () => {
    const isValid = await trigger();
    return isValid;
  }, [trigger]);

  useEffect(() => {
    if (resetOnOpen && open !== undefined && !open) {
      reset(defaultValues);
    }
  }, [open, defaultValues, reset, resetOnOpen]);

  useEffect(() => {
    const currentValues = getValues();
    memoizedCampaigns.forEach((campaign, index) => {
      const campaignKey = campaign.campaignId || index.toString();
      if (!currentValues[campaignKey]) {
        setValue(
          campaignKey,
          { responseCode: '', reason: '', type: '', competitor: '', notes: '' },
          { shouldValidate: false },
        );
      }
    });
  }, [memoizedCampaigns, getValues, setValue]);

  const sortedCompetitorOptions = useMemo(() => {
    if (!competitorLookup?.values) {
      return [];
    }
    const seen = new Set<string>();
    return competitorLookup.values
      .filter((item) => {
        const key = (item.label || '').toLowerCase().trim();
        if (!key || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [competitorLookup]);

  const getFilteredReasonLookup = useCallback(
    (
      responseCodeValue: string | null | undefined,
    ): { lookup: Lookup | undefined; hasReasons: boolean } => {
      const { lookup, hasValues } = getFilteredLookupForResponseCode(
        reasonLookup,
        responseCodeValue,
      );
      return { lookup, hasReasons: hasValues };
    },
    [reasonLookup],
  );

  const getFilteredTypeLookup = useCallback(
    (
      responseCodeValue: string | null | undefined,
    ): { lookup: Lookup | undefined; hasTypes: boolean } => {
      const { lookup, hasValues } = getFilteredLookupForResponseCode(typeLookup, responseCodeValue);
      return { lookup, hasTypes: hasValues };
    },
    [typeLookup],
  );

  const handleResponseCodeChange = useCallback(
    (campaignKey: string, value: string) => {
      const currentData = getValues()[campaignKey];
      const currentReason = currentData?.reason || '';
      const currentType = currentData?.type || '';

      if (!value) {
        setValue(
          campaignKey,
          { responseCode: '', reason: '', type: '', competitor: '', notes: '' },
          { shouldValidate: false },
        );
        clearErrors(campaignKey);
      } else {
        const validReasonValues = getValuesForResponseCode(reasonLookup, value);
        const hasReasons = validReasonValues.length > 0;
        const validTypeValues = getValuesForResponseCode(typeLookup, value);
        const hasTypes = validTypeValues.length > 0;
        const reasonStillValid =
          hasReasons &&
          currentReason &&
          isValueValidForResponseCode(reasonLookup, currentReason, value);
        const typeStillValid =
          hasTypes && currentType && isValueValidForResponseCode(typeLookup, currentType, value);

        setValue(
          campaignKey,
          {
            responseCode: value,
            reason: reasonStillValid ? currentReason : '',
            type: typeStillValid ? currentType : '',
            competitor: '',
            notes: '',
          },
          { shouldValidate: false },
        );
        clearErrors(campaignKey);
      }
    },
    [setValue, getValues, reasonLookup, typeLookup, clearErrors],
  );

  return {
    methods,
    validateForm,
    sortedCompetitorOptions,
    getFilteredReasonLookup,
    getFilteredTypeLookup,
    handleResponseCodeChange,
  };
};

